import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { OutOfOfficeForm } from '../components/workflows/OutOfOffice';
import { MeetingLinkForm } from '../components/candidates/MeetingLinkForm';
import { TimeZoneCard } from '../components/candidates/TimeZoneCard';
import { ToastProvider } from '../components/ui/Kit';

function SettingsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const fetchUserProfile = useCallback(async (token) => {
    try {
      const response = await fetch(API_ENDPOINTS.USERS_ME, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setUser((u) => ({ ...(u || {}), ...result.user }));   // the token has no company_id — /me does (shows the company cards)
        setProfileData({
          fullName: result.user.full_name,
          email: result.user.email,
          phone: result.user.phone || '',
          role: result.user.role
        });
      } else if (response.status === 401) {
        localStorage.clear();
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      
      setUser({
        id: tokenPayload.user_id,
        email: tokenPayload.email,
        full_name: tokenPayload.full_name,
        role: tokenPayload.role
      });

      fetchUserProfile(token);
    } catch (error) {
      console.error('Invalid token:', error);
      navigate('/');
    }
  }, [navigate, fetchUserProfile]);

  const handleUpdatePhone = async () => {
    const token = localStorage.getItem('token');
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch(API_ENDPOINTS.USERS_UPDATE(user.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone: profileData.phone
        })
      });

      if (response.ok) {
        setSuccessMessage('Phone updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const result = await response.json();
        setErrorMessage(result.detail || 'Failed to update');
      }
    } catch (error) {
      setErrorMessage('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    setSuccessMessage('');
    setErrorMessage('');

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setErrorMessage('All fields required');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setErrorMessage('Password must be 8+ characters');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    const token = localStorage.getItem('token');
    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.USERS_UPDATE(user.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: passwordData.newPassword,
          current_password: passwordData.currentPassword
        })
      });

      if (response.ok) {
        setSuccessMessage('Password changed successfully. Please use your new password next time you log in.');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const result = await response.json();
        setErrorMessage(result.detail || 'Failed to change password');
      }
    } catch (error) {
      setErrorMessage('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    if (user?.role === 'master_admin') {
      navigate('/dashboard/master-admin');
    } else if (user?.role === 'company_super_admin') {
      navigate('/dashboard/company-admin');
    } else {
      navigate('/dashboard/user');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      'master_admin': { text: 'Master Admin', color: 'bg-purple-100 text-purple-700' },
      'company_super_admin': { text: 'Super Admin', color: 'bg-blue-100 text-blue-700' },
      'company_user': { text: 'User', color: 'bg-green-100 text-green-700' }
    };
    const badge = badges[role] || badges['company_user'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-gray-600 hover:text-brand-blue mb-3 text-sm font-medium"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
              <p className="text-sm text-gray-600 mt-0.5">Manage your account</p>
            </div>
            <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center text-white font-bold text-lg">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6">
        {/* Messages */}
        {successMessage && (
          <div className="mb-4 bg-green-50 border-l-4 border-green-400 text-green-700 px-4 py-3 rounded text-sm">
            ✓ {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded text-sm">
            ✕ {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>👤</span> Profile
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    placeholder="+1 (555) 000-0000"
                  />
                  <button
                    onClick={handleUpdatePhone}
                    disabled={isLoading}
                    className="px-4 py-2 bg-brand-blue text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <div>{getRoleBadge(profileData.role)}</div>
              </div>
            </div>
          </div>

          {/* Change Password Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🔐</span> Change Password
            </h2>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="••••••••"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="Min. 8 characters"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="Re-enter password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-4 py-2.5 bg-brand-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 disabled:opacity-50 mt-2"
              >
                {isLoading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>

        {/* Out of office (bundle 3b): approvals + jobs to assign go to a delegate */}
        {user && user.company_id && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6" data-testid="settings-ooo">
            <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2"><span>🌴</span> Out of office</h2>
            <p className="text-sm text-gray-600 mb-4">While you're away, your approvals and jobs to assign also go to a colleague.</p>
            <ToastProvider><OutOfOfficeForm /></ToastProvider>
          </div>
        )}

        {/* Part E: your time zone, and where it came from */}
        {user && user.company_id && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6" data-testid="settings-timezone">
            <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2"><span>🕒</span> Your time zone</h2>
            <p className="text-sm text-gray-600 mb-3">Follows where you work (your office, or your home city when you work remotely) unless you change it. Interview times are shown in it.</p>
            <TimeZoneCard user={user} onSaved={(patch) => setUser((u) => ({ ...u, ...patch }))} />
          </div>
        )}

        {/* Part B: the personal meeting link that pre-fills video rounds you lead */}
        {user && user.company_id && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6" data-testid="settings-meeting-link">
            <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2"><span>🎥</span> Your meeting link</h2>
            <p className="text-sm text-gray-600 mb-4">Pre-fills the meeting link when a video interview you lead is scheduled. It can still be changed per interview.</p>
            <ToastProvider><MeetingLinkForm /></ToastProvider>
          </div>
        )}
      </main>
    </div>
  );
}

export default SettingsPage;